<template>
  <div
    ref="list"
    class="sp-list"
    :role="listRole"
    :aria-multiselectable="listMultiselectable"
    :aria-label="props.ariaLabel"
    :aria-labelledby="listLabelId"
    tabindex="-1"
  >
    <sp-list-subheader
      v-if="props.subheader"
      :id="listLabelId"
      :role="subheaderRole"
      :title="props.subheader"
    ></sp-list-subheader>

    <template v-for="(section, sectionIndex) in sections" :key="sectionIndex">
      <div v-if="section.groupLabelId" role="group" :aria-labelledby="section.groupLabelId">
        <sp-list-subheader :id="section.groupLabelId" :role="subheaderRole" :title="section.header"></sp-list-subheader>
        <sp-list-item
          v-for="entry in section.entries"
          :key="entry.index"
          v-bind="itemBindings(entry)"
          @click="handleRowClick(entry)"
          @append-action="emit('append-action', entry.item)"
        ></sp-list-item>
      </div>
      <template v-else>
        <sp-list-subheader v-if="section.hasHeader" :role="subheaderRole" :title="section.header"></sp-list-subheader>
        <sp-list-item
          v-for="entry in section.entries"
          :key="entry.index"
          v-bind="itemBindings(entry)"
          @click="handleRowClick(entry)"
          @append-action="emit('append-action', entry.item)"
        ></sp-list-item>
      </template>
    </template>
  </div>
</template>

<script setup>
import { onKeyStroke, useFocusWithin, useThrottleFn, watchImmediate } from "@vueuse/core";
import { computed, onUnmounted, ref, useTemplateRef, watch } from "vue";
import { toArray, toBoolean } from "#ce/utils/props";
import { useExpose } from "#ce/composables/useExpose";
import { useHostEmit } from "#ce/composables/useHostEmit";
import { list as listProps } from "./props";
import { useSpList } from "./composables/useSpList";
import { useListRole } from "./composables/useListRole";
import { useScrollSelectedIntoView } from "./composables/useScrollSelectedIntoView";
import { useTypeAhead } from "./composables/useTypeAhead";

const emit = defineEmits(["update-selected", "select-item", "append-action", "exit-start"]);
const { emitBubbling } = useHostEmit();

const props = defineProps({
  ...listProps,
});

const {
  getItemProps,
  getItemTitle,
  getItemValue,
  getItemSubtitle,
  getItemTags,
  isHeader,
  isSelectable,
  isReadonly,
  isItemDisabled,
  indexOfListItem,
  listItemOfIndex,
  listItemIndexes,
} = useSpList(props);

const list = useTemplateRef("list");

/**
 * Check if the list has items with a prepend visual — an icon (`prependIcon` /
 * `prependIconUrl`) or an avatar (`prependAvatar`). If the selectedPrependIcon
 * prop is defined or any item has one, it will return true.
 *
 * This is used to determine if the list items should have left indentation, so
 * mixed lists (some rows with a prepend visual, some without) align consistently.
 */
const PREPEND_VISUAL_KEYS = ["prependIcon", "prependIconUrl", "prependAvatar"];
const hasItemsWithPrependVisual = computed(
  () =>
    props.selectedPrependIcon !== undefined ||
    props.items?.some((item) => {
      const itemProps = getItemProps(item);
      // Truthy, not just present: SpListItem renders a visual only for a
      // non-empty value, so `prependIconUrl: ""` must not trigger indentation.
      return PREPEND_VISUAL_KEYS.some((key) => Boolean(itemProps[key]));
    }),
);

/**
 * Check if the list has items with an append icon or an append caret.
 * If any item has either, it will return true.
 *
 * This is used to determine if the list items should have right indentation.
 */
const hasItemsWithAppendIcon = computed(() =>
  props.items?.some((item) => {
    const itemProps = getItemProps(item);
    return "appendIcon" in itemProps || toBoolean(itemProps.appendCaret);
  }),
);

/**
 * If `listenOnKeyEvent` is true, the list will listen to key events and navigate through the list.
 * If `listenOnKeyEvent` is "focus", the list will listen to key events only while focus is inside
 * it, which includes a focused row.
 * Otherwise, it will not listen to key events and all existing key event listeners will be removed.
 */
const { focused } = useFocusWithin(list);
const listenOnKeyEvent = computed(
  () => toBoolean(props.listenOnKeyEvent) || (props.listenOnKeyEvent === "focus" && focused.value),
);

/**
 * The key stroke listeners for navigation and selection.
 * These listeners will be removed when the component is unmounted or when `listenOnKeyEvent` is false.
 */
let navigationKeyStrokeListener;
let selectionKeyStrokeListener;
let typeAheadKeyStrokeListener;

const selectionKeys = ["Enter", " "];
const navigationKeys = ["ArrowDown", "ArrowUp", "Home", "End"];

// A disabled row is not navigable, so it is not a type-ahead target either.
const { handleKeyStroke: handleTypeAheadKey, reset: resetTypeAhead } = useTypeAhead({
  labels: () =>
    listItemIndexes.value.map((_, index) => {
      const item = listItemOfIndex(index);
      return isItemDisabled(item) ? null : renderedItemTitle(item);
    }),
  currentIndex: focusedRowIndex,
  onMatch: focusIndex,
});

function handleTypeAheadKeyStroke(e) {
  // A control inside a row answers the keystroke itself, and the search the visitor was in the
  // middle of ends there — carried over, it would prepend itself to their next one.
  if (originatesFromNestedControl(e) || originatesFromEditableTextEntry(e)) {
    resetTypeAhead();
    return;
  }

  handleTypeAheadKey(e);
}

watch(
  listenOnKeyEvent,
  (value) => {
    if (value) {
      selectionKeyStrokeListener = onKeyStroke(selectionKeys, handleSelectionKeyStroke, { dedupe: true });
      navigationKeyStrokeListener = onKeyStroke(navigationKeys, handleNavigationKeyStroke);
      typeAheadKeyStrokeListener = onKeyStroke(true, handleTypeAheadKeyStroke);
    } else {
      selectionKeyStrokeListener?.();
      navigationKeyStrokeListener?.();
      typeAheadKeyStrokeListener?.();
      // The next focus session is a new search, not a continuation of the half-typed one.
      resetTypeAhead();
    }
  },
  { immediate: true },
);

// Ensure listeners are cleaned up on unmount
onUnmounted(() => {
  selectionKeyStrokeListener?.();
  navigationKeyStrokeListener?.();
  typeAheadKeyStrokeListener?.();
});

// Public keyboard entry point: move roving focus to the first / last navigable
// option. Focuses the DOM row explicitly so it applies even when the index is
// already at the target (the watcher below only fires on a changed index).
const { exposeMethods, exposeProperties } = useExpose();
exposeMethods({
  // Selects a selectable item the way a click does, so the pick reports through `update-selected`.
  // Returns whether the selection changed: false for an unselectable or an already selected item,
  // which stays selected.
  selectItem(item) {
    if (!isSelectable(item) || isSelected(item)) {
      return false;
    }

    handleItemClick(item);
    return true;
  },
  // Moves roving focus to a row by its navigation index, so a caller stepping the tree leaves the
  // list's own arrow keys resuming from where it landed.
  focusItemAt(index) {
    const target = Number(index);

    if (!Number.isInteger(target) || target < 0 || target >= listItemIndexes.value.length) {
      return false;
    }

    if (focusedIndex.value === target) {
      focusRow(target);
    } else {
      focusedIndex.value = target;
    }

    return true;
  },
  focusItem(location) {
    const previousIndex = focusedIndex.value;
    focus(location === "last" ? "End" : "Home");
    // The focusedIndex watcher already focuses the row when the index changes;
    // only drive it here when the index is unchanged (watcher won't re-run).
    if (focusedIndex.value === previousIndex) {
      focusRow(focusedIndex.value);
    }
  },
});

function getItemTabindex(index) {
  return focusedIndex.value === indexOfListItem(index) ? 0 : -1;
}

// Reflect aria-disabled onto the focusable `sp-list-item` host (not just the
// inner shadow-DOM node), and omit it entirely for enabled rows so they carry
// no `aria-disabled="false"`.
function itemAriaDisabled(item) {
  return isItemDisabled(item) ? "true" : undefined;
}

// Bind a normalized boolean so the host `[disabled]` attribute matches
// `isItemDisabled`/`isSelectable`, and omit it entirely for enabled rows so
// they carry no `disabled="false"`.
function itemDisabledAttr(item) {
  return isItemDisabled(item) ? true : undefined;
}

// The selection-key listener is window-level, so Space/Enter typed into a
// text-entry element (e.g. an external search field driving this list) must
// reach that field instead of being cancelled as a selection keystroke.
// Keyboard selection is unaffected: arrow-key navigation moves real focus
// onto a list row first, and a row is not text entry.
function originatesFromTextEntry(e) {
  const origin = e.composedPath?.()[0] ?? e.target;
  return (
    origin instanceof HTMLElement &&
    (origin.tagName === "INPUT" || origin.tagName === "TEXTAREA" || origin.isContentEditable)
  );
}

// A readonly field takes focus but no text, so it holds focus for the list rather than competing
// for the keystroke. The default `sp-select` activator is one.
function originatesFromEditableTextEntry(e) {
  const origin = e.composedPath?.()[0] ?? e.target;

  return originatesFromTextEntry(e) && !origin.readOnly && !origin.disabled;
}

// A row can hold a control of its own — the disclosure caret. Focus sits on that control, inside
// the list, so the keys it answers to are its own rather than the row's.
const NESTED_CONTROL = "button, a[href], input, select, textarea, summary";

function originatesFromNestedControl(e) {
  const path = e.composedPath?.() ?? [];
  const row = path.findIndex((node) => node?.tagName?.toLowerCase?.() === "sp-list-item");

  if (row < 0) {
    return false;
  }

  return path.slice(0, row).some((node) => node instanceof HTMLElement && node.matches?.(NESTED_CONTROL));
}

function handleSelectionKeyStroke(e) {
  if (originatesFromTextEntry(e) || originatesFromNestedControl(e)) {
    return;
  }

  e.stopPropagation();
  e.preventDefault();
  return conditionallyUpdateSelection();
}

// A held arrow key repeats at the OS key-repeat rate, which users can set as
// fast as ~30 rows a second — quicker than the eye can follow or the hand can
// release on a target. Auto-repeat runs at a fixed cadence instead; a discrete
// press stays immediate, so deliberate taps are never dropped.
const REPEATED_NAVIGATION_INTERVAL_MS = 80;
const focusOnRepeat = useThrottleFn(focus, REPEATED_NAVIGATION_INTERVAL_MS);

function handleNavigationKeyStroke(e) {
  if (originatesFromNestedControl(e)) {
    return;
  }

  e.preventDefault();
  e.stopPropagation();

  if (e.repeat) {
    focusOnRepeat(e.key);
  } else {
    focus(e.key);
  }
}

useScrollSelectedIntoView(list, () => toBoolean(props.scrollSelectedIntoView));

const focusedIndex = ref(-1);

// Move real DOM focus (and scroll) to the row at `index`. Driven by the
// focusedIndex watcher and called directly by focusItem for the unchanged-index case.
function focusRow(index) {
  if (index < 0) {
    return;
  }
  const el = rowElements()[index];
  const behavior = index === 0 || index === listItemIndexes.value.length - 1 ? "auto" : "smooth";
  el?.focus();
  el?.scrollIntoView({ block: "nearest", inline: "nearest", behavior });
}

watch(focusedIndex, focusRow);

// Rows in navigation order. A header renders as `sp-list-subheader`, so position is the navigation
// index — unlike the row's `id`, which a consumer's `props` can replace.
function rowElements() {
  return [...(list.value?.querySelectorAll("sp-list-item") ?? [])];
}

// A direct `.focus()` on a row, and a mousedown that never becomes a click, both move focus
// without writing the roving index, so read the row that actually holds it.
function focusedRowIndex() {
  const active = list.value?.getRootNode()?.activeElement;
  const row = active?.closest?.("sp-list-item");

  return row ? rowElements().indexOf(row) : focusedIndex.value;
}

// The watcher does not re-run for an unchanged index, so drive the row directly in that case.
function focusIndex(index) {
  if (focusedIndex.value === index) {
    focusRow(index);
  } else {
    focusedIndex.value = index;
  }
}

/**
 * Focus the list item based on the key.
 *
 * If the key is "Home", the first item will be focused.
 * If the key is "End", the last item will be focused.
 * If the key is "ArrowDown", the next item will be focused. If the last item is focused, the first item will be focused.
 * If the key is "ArrowUp", the previous item will be focused. If the first item is focused, the last item will be focused.
 *
 * @param {KeyboardEvent} e
 * @param {string} key
 */
function focus(key) {
  const numberOfItems = listItemIndexes.value.length;

  if (numberOfItems === 0) {
    focusedIndex.value = -1;
    return;
  }

  if (key === "Home") {
    focusedIndex.value = findNavigableIndex(0, 1);
  } else if (key === "End") {
    focusedIndex.value = findNavigableIndex(numberOfItems - 1, -1);
  } else if (key === "ArrowDown") {
    focusedIndex.value = findNavigableIndex(focusedIndex.value + 1, 1);
  } else if (key === "ArrowUp") {
    // With `exitStart`, Arrow Up on the first navigable row hands control back
    // to the host instead of wrapping, and forgets the roving position so the
    // next Arrow Up wraps to the last row (focusedIndex -1 starts from the
    // end below).
    if (toBoolean(props.exitStart) && focusedIndex.value !== -1 && focusedIndex.value === findNavigableIndex(0, 1)) {
      focusedIndex.value = -1;
      emit("exit-start");
      return;
    }
    // When nothing is focused yet (focusedIndex === -1), ArrowUp wraps to the
    // last item — so start the search from the last index rather than -2.
    const from = focusedIndex.value <= 0 ? numberOfItems - 1 : focusedIndex.value - 1;
    focusedIndex.value = findNavigableIndex(from, -1);
  }
}

/**
 * Starting at `from`, walk in the `step` direction (with wrap-around) and
 * return the first navigation index whose item is not disabled, so keyboard
 * navigation lands only on actionable rows. Returns `-1` when every row is
 * disabled (no navigable target).
 *
 * @param {number} from - The navigation index to start searching from.
 * @param {number} step - The direction to walk in (+1 forward, -1 backward).
 * @returns {number} The first non-disabled navigation index, or -1 if none.
 */
function findNavigableIndex(from, step) {
  const numberOfItems = listItemIndexes.value.length;

  for (let offset = 0; offset < numberOfItems; offset++) {
    const index = (((from + step * offset) % numberOfItems) + numberOfItems) % numberOfItems;
    if (!isItemDisabled(listItemOfIndex(index))) {
      return index;
    }
  }

  return -1;
}

const multiple = computed(() => toBoolean(props.multiple));
const checkboxes = computed(() => toBoolean(props.checkboxes));
const required = computed(() => toBoolean(props.required));

const model = ref(props.selected ? toArray(props.selected) : []);
watch(
  () => props.selected,
  (value) => (model.value = toArray(value)),
);

// Reports the selection the visitor just made, synchronously with the click or keypress that made
// it. Every write reassigns the ref, so an unchanged reference means the item was not selectable
// and there is nothing to report. The `selected` prop sync above writes the model without coming
// through here, so it stays silent.
function selectByUser(item) {
  const reported = model.value;

  updateSelection(item);

  if (model.value !== reported) {
    emit("update-selected", model.value);
  }
}

function conditionallyUpdateSelection() {
  if (focusedIndex.value >= 0) {
    const item = listItemOfIndex(focusedIndex.value);
    selectByUser(item);
    emit("select-item", item);
  }
}

const { listRole, isListbox, isTree } = useListRole({
  items: () => props.items,
  readonly: isReadonly,
  tree: () => props.tree,
  isHeader,
  getItemProps,
});

// Bubbles, so an ancestor in this shadow root hears it without holding a ref to this element.
function reportListRole(role) {
  emitBubbling("update-role", role);
}

// Read by a panel that is shown again without re-rendering, which reports for the list rather than
// waiting for a change that is not coming.
exposeProperties({
  listRole: {
    configurable: true,
    get: () => listRole.value,
  },
});

watchImmediate(listRole, reportListRole);

// A tree owns only `treeitem`s and `group`s, so a subheader that is not wrapped in a named group
// cannot sit among them as itself. It stays visible and becomes presentational in the structure;
// the top one also names the list, as it does for a listbox.
const subheaderRole = computed(() => (isTree.value ? "presentation" : undefined));
const listMultiselectable = computed(() => {
  if (isTree.value) {
    return multiple.value;
  }

  return isListbox.value ? multiple.value : undefined;
});

function getItemRole() {
  if (isTree.value) {
    return "treeitem";
  }

  return isListbox.value ? "option" : undefined;
}

function getItemAriaSelected(item) {
  return isTree.value || isListbox.value ? isSelected(item) : undefined;
}

// A row that renders a disclosure caret is a branch, and a tree states the state on the row itself.
// `appendCaret` marks the branch in both accordion modes; only the selectable-branch mode also
// makes the caret its own control.
//
// An open branch that renders no child is a leaf after all — its children resolved to none, or a
// query pruned them — and a leaf states no expansion. A closed one keeps the state: its children
// are unrendered because it is closed, which is exactly what `aria-expanded="false"` says.
function getItemAriaExpanded(item, index) {
  const itemProps = getItemProps(item);

  if (!isTree.value || !toBoolean(itemProps?.appendCaret)) {
    return undefined;
  }

  const expanded = toBoolean(itemProps?.appendCaretExpanded);

  if (expanded && !hasRenderedChild(index)) {
    return undefined;
  }

  return expanded;
}

function hasRenderedChild(index) {
  const rows = itemTreePositions.value;
  const navigationIndex = indexOfListItem(index);
  const row = rows[navigationIndex];

  return Boolean(row) && (rows[navigationIndex + 1]?.level ?? 0) > row.level;
}

// Depth, position and size of every navigable row, walked once.
//
// Depth comes from the row's `level`, honoured only when a row that is itself where it declared to
// be is open one level above: a search that reorders or truncates results can render a child where
// its parent is not, and such a row is a root of what is rendered rather than a level nothing owns.
//
// A row's set runs until a shallower row ends it, and a named section header starts fresh sets at
// every level — a blank header names no group, so it does not break the run either. Each row holds
// its run, whose size is read back once the walk ends, so a long run costs one pass.
const itemTreePositions = computed(() => {
  const rows = [];
  let openSets = new Map();
  let parentable = new Set();
  let owned = false;

  (props.items ?? []).forEach((item) => {
    if (isHeader(item)) {
      // Deferred until a row arrives, because only then is the header known to own one — the same
      // test that decides whether it names a group. The nearest header owns the row, so a later
      // blank one takes the decision back.
      owned = String(item.header ?? "").trim().length > 0;

      return;
    }

    if (owned) {
      openSets = new Map();
      parentable = new Set();
      owned = false;
    }

    const declared = Math.max(Math.round(Number(getItemProps(item)?.level)) || 1, 1);
    // A row nests only under one that is itself where it declared to be. A row rendered without its
    // parent is a root, and cannot become a parent in its place — otherwise two children torn from
    // different branches would read as one nested under the other.
    const level = declared > 1 && parentable.has(declared - 1) ? declared : 1;

    // Only an open branch has children rendered under it, and only a row that is where it declared
    // to be can own them, so only such a row leaves its level open to the rows that follow.
    const itemProps = getItemProps(item);
    const opensChildren =
      level === declared && toBoolean(itemProps?.appendCaret) && toBoolean(itemProps?.appendCaretExpanded);

    if (opensChildren) {
      parentable.add(level);
    } else {
      parentable.delete(level);
    }

    [...parentable.keys()].filter((open) => open > level).forEach((open) => parentable.delete(open));
    [...openSets.keys()].filter((open) => open > level).forEach((open) => openSets.delete(open));

    let run = openSets.get(level);

    if (!run) {
      run = { size: 0 };
      openSets.set(level, run);
    }

    run.size += 1;
    rows.push({ level, position: run.size, run });
  });

  return rows.map(({ level, position, run }) => ({ level, position, size: run.size }));
});

function getItemTreeBindings(index) {
  if (!isTree.value) {
    return {};
  }

  const row = itemTreePositions.value[indexOfListItem(index)];

  return {
    "aria-level": row?.level,
    "aria-posinset": row?.position,
    "aria-setsize": row?.size,
  };
}

// Splits `items` into the sections its `header` entries mark out; the first section carries no
// header when rows precede the first one. `groupLabelId` is set only where a listbox or a tree may
// own the rows through a `role="group"`, which needs a name and at least one row. `sp-list-subheader`
// hides itself when its trimmed title is empty, so a blank header names nothing. `hasHeader` is
// tracked apart from the value because `{ header: undefined }` is a header entry that still
// renders its host, which the `sp-list-subheader + sp-list-item` divider rule matches on. The id is
// namespaced because a row carries whatever `id` its item props supply, and an `aria-labelledby`
// resolves to the first match in the shadow root. A random suffix, matching SpRadioGroup: Vue's
// useId() is per app, and each custom element mounts as its own, so every list would share "v-0".
const sectionIdPrefix = `sp-list-section-${Math.random().toString(36).slice(2, 10)}`;

// A subheader names the list when nothing else does. An explicit `aria-label` wins, so a picker's
// own name is not overridden by a heading inside the popup.
const listLabelId = computed(() =>
  !props.ariaLabel && String(props.subheader ?? "").trim().length > 0 ? `${sectionIdPrefix}-label` : undefined,
);
const sections = computed(() => {
  const built = [];

  (props.items ?? []).forEach((item, index) => {
    if (isHeader(item) || built.length === 0) {
      built.push({ hasHeader: isHeader(item), header: isHeader(item) ? item.header : undefined, entries: [] });
    }

    if (!isHeader(item)) {
      built[built.length - 1].entries.push({ item, index });
    }
  });

  return built.map((section, sectionIndex) => ({
    ...section,
    groupLabelId:
      (isListbox.value || isTree.value) && String(section.header ?? "").trim().length > 0 && section.entries.length > 0
        ? `${sectionIdPrefix}-${sectionIndex}`
        : undefined,
  }));
});

// `itemBindings` spreads `getItemProps` after `title`, so a declared `props.title` is the one the
// row renders — including a null one, which renders no title at all.
function renderedItemTitle(item) {
  const itemProps = getItemProps(item);

  return itemProps && "title" in itemProps ? itemProps.title : getItemTitle(item);
}

function itemBindings({ item, index }) {
  return {
    id: indexOfListItem(index),
    role: getItemRole(item),
    tabindex: getItemTabindex(index),
    active: isSelected(item),
    checkbox: checkboxes.value,
    "active-prepend-icon": props.selectedPrependIcon,
    "active-prepend-icon-fill-color": props.selectedPrependIconFillColor,
    "aria-selected": getItemAriaSelected(item),
    "aria-expanded": getItemAriaExpanded(item, index),
    ...getItemTreeBindings(index),
    title: getItemTitle(item),
    subtitle: getItemSubtitle(item),
    tags: getItemTags(item),
    value: getItemValue(item),
    "left-indentation": hasItemsWithPrependVisual.value,
    "right-indentation": hasItemsWithAppendIcon.value,
    readonly: props.readonly,
    wrap: props.wrap,
    ...getItemProps(item),
    disabled: itemDisabledAttr(item),
    "aria-disabled": itemAriaDisabled(item),
  };
}

function isSelected(item) {
  if (!isSelectable(item)) {
    return false;
  }

  const value = getItemValue(item);
  return model.value.some((selected) => isItemValue(selected, value));
}

// A row a visitor pointed at. The tab stop follows it, unless it is disabled — the arrows never
// land there, so the stop would sit where the keyboard cannot reach.
function handleRowClick({ item, index }) {
  if (!isItemDisabled(item)) {
    focusedIndex.value = indexOfListItem(index);
  }

  handleItemClick(item);
}

function handleItemClick(item) {
  emit("select-item", item);
  selectByUser(item);
}

function updateSelection(item) {
  if (!isSelectable(item)) {
    return;
  }

  toggleSelection(item);
}

/**
 * Toggle the selection of the item.
 *
 * If the multiple prop is true, the item will be added to the selection if it is not selected.
 * If the multiple prop is true, the item will be removed from the selection if it is selected.
 *
 * If the multiple prop is false, the item will be selected if it is not selected.
 * If the multiple prop is false, the item will be unselected if it is selected.
 *
 * @param {Object} item
 */
function toggleSelection(item) {
  if (multiple.value) {
    toggleMultipleSelection(item);
  } else {
    toggleSingeSelection(item);
  }
}

function toggleMultipleSelection(item) {
  const value = getItemValue(item);
  const index = model.value.findIndex((entry) => isItemValue(value, entry));

  // Reassign the ref with a new array so the shallow `watch(model, ...)` above
  // fires and `update-selected` reaches the consumer. In-place push / splice
  // mutate the same array reference, and the watcher doesn't observe that.
  if (index === -1) {
    model.value = [...model.value, value];
  } else if (!(required.value && model.value.length === 1)) {
    // Keep the last remaining selection when required so the list is never empty.
    model.value = model.value.filter((_, i) => i !== index);
  }
}

function toggleSingeSelection(item) {
  const value = getItemValue(item);
  if (isItemValue(model.value?.[0], value)) {
    // Re-selecting the current item clears it, unless a selection is required.
    if (!required.value) {
      model.value = [];
    }
  } else {
    model.value = [value];
  }
}

function isItemValue(value, other) {
  return String(other) === String(value);
}
</script>

<style>
:host {
  display: block;
  --list-min-width: var(--sp-comp-list-min-width, auto);
}
</style>

<style scoped lang="scss">
.sp-list {
  min-width: var(--list-min-width);
  width: 100%;
}

sp-list-item:hover:is(:not([active]), [checkbox]) + sp-list-item,
sp-list-item:focus:is(:not([active]), [checkbox]) + sp-list-item,
sp-list-item:focus-visible:is(:not([active]), [checkbox]) + sp-list-item {
  --divider-width: 100%;
  --sp-comp-divider-color: var(--sp-comp-list-item-hover-background-color, #ccc);
  --divider-left: 0;
}

/* Same divider blend for the row after a highlighted row, but chained through
   the highlighted token first so a theme that sets it apart from hover keeps a
   matching divider. */
sp-list-item[highlighted]:is(:not([active]), [checkbox]) + sp-list-item {
  --divider-width: 100%;
  --sp-comp-divider-color: var(
    --sp-comp-list-item-highlighted-background-color,
    var(--sp-comp-list-item-hover-background-color, #ccc)
  );
  --divider-left: 0;
}

sp-list-subheader + sp-list-item,
sp-list-item:first-child {
  --display-divider: none;
}
sp-list-item[active] + sp-list-item {
  --display-divider: var(
    --sp-comp-list-item-active-display-next-divider,
    var(--sp-comp-list-item-display-divider, block)
  );
}
/* A checkbox row keeps a plain background, so the row after it keeps its divider. */
sp-list-item[active][checkbox] + sp-list-item {
  --display-divider: var(
    --sp-comp-list-item-checkbox-row-active-display-next-divider,
    var(--sp-comp-list-item-display-divider, block)
  );
}
</style>
